import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Box, CircularProgress, TextField, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

import { Card } from '../../sections/@dashboard/find-edifier';
import { getUserDetails, sortByLname } from '../../utils';
import { useQueryGetPrincipalsList, useQueryDeletePrincipal, useMutationCreatePrincipal, useMutationUpdatePrincipal } from '../../hooks/react-query/useData';
import { AddPrincipalDialog, UserDetailedInformationDialog, DeleteDialog, DeleteOperationDialog } from '../../components/dialog';
import CustomPagination from '../../components/pagination/CustomPagination';
import { useQueryGetSchoolsList } from '../../hooks/react-query/schools';
import EmptyState from '../../components/empty-state';

const ManagePrincipals = () => {
  const userDetails = getUserDetails();
  const userToken = userDetails.token;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [addPrincipalOpen, setAddPrincipalOpen] = useState(false);
  const [deletePrincipalOpen, setDeletePrincipalOpen] = useState(false);
  const [deleteOperationOpen, setDeleteOperationOpen] = useState(false);
  const [editPrincipalOpen, setEditPrincipalOpen] = useState(false);

  const [displayUserDetails, setDisplayUserDetails] = useState(false);
  const [currentUser, setCurrentUser] = useState({});
  const [principalData, setPrincipalData] = useState({});
  const [principalToEdit, setPrincipalToEdit] = useState({});
  const [itemToDeleteId, setItemToDeleteId] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search state
  const [searchedValue, setSearchedValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');


  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Search functions
  const searchFromRecord = () => {
    setSearchQuery(searchedValue);
    setPage(0);
  };

  const clearSearch = () => {
    setSearchedValue('');
    setSearchQuery('');
    setPage(0);
  };

  // Fetch principals list using dedicated Principal API
  const { data: principalsList, isFetching: isLoading } = useQueryGetPrincipalsList(
    {
      token: userDetails?.token,
      page: page + 1,
      limit: rowsPerPage,
      ...(searchQuery && { search: searchQuery }),
    },
    navigate,
    toast
  );

  // Fetch schools list for filter
  const { data: schoolList } = useQueryGetSchoolsList();

  // Handler functions
  const handleClose = () => {
    setAddPrincipalOpen(false);
    setPrincipalData({});
  };

  // Add principal mutation using react-query
  const { isLoading: isCreating, mutate: createPrincipalMutation } = useMutationCreatePrincipal(
    principalData,
    toast,
    handleClose,
    userToken,
    queryClient,
    navigate
  );

  // Delete principal using react-query
  const { isLoading: isDeleting, refetch: deletePrincipal } = useQueryDeletePrincipal(
    { token: userToken, _id: itemToDeleteId },
    navigate,
    setDeleteOperationOpen,
    toast,
    queryClient
  );

  // Update principal mutation
  const { isLoading: isUpdating, mutate: updatePrincipalMutation } = useMutationUpdatePrincipal(
    { principalId: principalToEdit?._id, data: principalData },
    toast,
    () => {
      setEditPrincipalOpen(false);
      setPrincipalToEdit({});
      setPrincipalData({});
    },
    userToken,
    setPrincipalToEdit,
    navigate,
    queryClient
  );

  // Effect to add principal when data is ready
  useEffect(() => {
    if (Object.keys(principalData).length > 0 && principalData.fname && !isCreating && !isUpdating) {
      if (editPrincipalOpen && principalToEdit?._id) {
        // Trigger update mutation
        updatePrincipalMutation();
      } else {
        // Trigger create mutation
        createPrincipalMutation();
      }
    }
  }, [principalData, editPrincipalOpen, principalToEdit]);

  const handleDeleteClose = () => {
    setDeletePrincipalOpen(false);
    setItemToDeleteId('');
  };

  const handleDeleteOperationClose = () => {
    setDeleteOperationOpen(false);
  };

  // Principals list from API (already filtered by role)
  const principalsData = principalsList?.data?.data?.users || [];

  const userCard = (index, list) => (
    <Card
      key={index}
      data={list}
      index={index}
      setDisplayUserDetails={setDisplayUserDetails}
      setCurrentUser={setCurrentUser}
      userRole={'admin'} // Explicitly pass as lowercase to match Card component checks
      setOpen={setDeletePrincipalOpen}
      setItemToDeleteId={setItemToDeleteId}
      setEdifierToEditData={setPrincipalToEdit}
      setEditEdifierOpen={setEditPrincipalOpen}
      isPrincipalCard // Flag to show only Edit and Delete options
    />
  );

  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Manage Principals</title>
      </Helmet>

      {/* Dialogs */}
      <AddPrincipalDialog
        open={addPrincipalOpen}
        handleClose={handleClose}
        setPrincipalData={setPrincipalData}
        schoolList={schoolList?.data?.data || []}
        isLoading={isCreating}
      />
      <AddPrincipalDialog
        open={editPrincipalOpen}
        handleClose={() => {
          setEditPrincipalOpen(false);
          setPrincipalToEdit({});
          setPrincipalData({});
        }}
        setPrincipalData={setPrincipalData}
        schoolList={schoolList?.data?.data || []}
        isLoading={isUpdating}
        isEdit
        principalToEdit={principalToEdit}
        setPrincipalToEdit={setPrincipalToEdit}
      />

      <UserDetailedInformationDialog
        open={displayUserDetails}
        handleClose={() => {
          setDisplayUserDetails(false);
          setCurrentUser({});
        }}
        userData={currentUser}
      />

      <DeleteOperationDialog
        open={deleteOperationOpen}
        handleDeleteOperationClose={handleDeleteOperationClose}
        isDeleting={isDeleting}
      />

      <DeleteDialog
        open={deletePrincipalOpen && itemToDeleteId !== ''}
        handleClose={handleDeleteClose}
        deleteCoach={deletePrincipal}
        setDeleteOperationOpen={setDeleteOperationOpen}
        deletePrincipal
      />

      <Container maxWidth="xl" sx={{ p: '0 !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
            Manage Principals
          </Typography>

          <Button
            variant="contained"
            color="main"
            onClick={() => setAddPrincipalOpen(true)}
            sx={{ height: 'fit-content', my: 'auto' }}
          >
            Add Principal
          </Button>
        </Box>

        {/* Search Section */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
          mb: 2
        }}>
          <Box sx={{ 
            display: 'flex',
            columnGap: '17px',
            flexDirection: { xs: 'column', md: 'row' },
            rowGap: '20px',
            alignItems: 'center'
          }}>
            <TextField
              placeholder="Search Principals by Name"
              className="filters"
              size="small"
              value={searchedValue}
              sx={{ 
                minWidth: '300px', 
                maxWidth: { xs: '100%', md: '300px' },
                '& .MuiInputBase-root': {
                  height: '40px'
                },
                '& .MuiInputBase-input': {
                  height: '40px',
                  py: 0
                }
              }}
              onChange={(e) => setSearchedValue(e.target.value.toLowerCase())}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  searchFromRecord();
                }
              }}
            />

            <Button
              variant="contained"
              color="main"
              size="small"
              onClick={() => searchFromRecord()}
              sx={{ 
                width: 'max-content',
                height: '40px',
                minHeight: '40px',
                maxHeight: '40px',
                lineHeight: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              Search
            </Button>

            {(searchQuery || searchedValue) && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={clearSearch}
                sx={{ 
                  width: 'max-content',
                  height: '40px',
                  minHeight: '40px',
                  lineHeight: 1
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', mt: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (!principalsData || principalsData.length === 0) && (
          <EmptyState
            icon={SupervisorAccountIcon}
            title="No Principals Found"
            description={searchQuery
              ? `No principals found matching "${searchQuery}". Try a different search term.`
              : "You haven't added any principals yet. Start by adding your first principal to manage school leadership."}
            iconClassName="principal-icon"
            dotsClassName="principal-dots"
          />

        )}

        {!isLoading && principalsData && principalsData.length > 0 && (
          <Grid container spacing={'28.47px'} sx={{ mt: '20px' }}>
            {sortByLname(principalsData)?.map((list, index) => userCard(index, list))}
          </Grid>
        )}

        {/* Pagination Controls */}
        {principalsData && principalsData.length > 0 && (
          <Box sx={{ mt: '20px' }}>
            <CustomPagination
              count={principalsList?.data?.data?.pagination?.totalCount || principalsData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Profiles per page:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default ManagePrincipals;

